import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { closeMoveResultModal } from 'redux/slices/moveResultModalSlice';
import { useGetMoveResultByIdQuery } from 'services/content/custom-blocks';
import { useUpdateAppUserNotesMutation } from 'services/user-management/app-users/appUsersAPIs';
import { formatPrescription } from 'utils/prescription';

const GlobalMoveResultModal = () => {
  const dispatch = useDispatch();
  const { isOpen, moveResultId } = useSelector((state) => state.moveResultModal);

  const { data, isLoading } = useGetMoveResultByIdQuery(
    { id: moveResultId },
    { skip: !isOpen || !moveResultId }
  );

  const handleClose = () => dispatch(closeMoveResultModal());

  // Coach note, written WITHOUT leaving the assessment. Reviewing a client's
  // videos used to mean closing this modal to log a limitation in the Notes
  // column, then re-opening and re-watching to continue. The note typed here is
  // APPENDED to the client's existing notes (users/notes PUT replaces the whole
  // text, which is why `userId.notes` is populated on this query), stamped with
  // the date and the exercise name so it reads as a reference later:
  //   — 17 Aug 2026 · Active Side Lying Shoulder IR Test
  //   limited IR on left, compensates with scapular tilt
  const [noteDraft, setNoteDraft] = React.useState('');
  const [savedNotes, setSavedNotes] = React.useState(null); // local echo after saves
  const [updateNotes, { isLoading: isSavingNote }] = useUpdateAppUserNotesMutation();
  React.useEffect(() => {
    // Reset per result opened, and adopt the server's notes as the base.
    setNoteDraft('');
    setSavedNotes(null);
  }, [moveResultId]);

  const currentNotes = savedNotes !== null ? savedNotes : data?.userId?.notes || '';

  const saveNote = async (exerciseName) => {
    const text = noteDraft.trim();
    const clientId = data?.userId?._id;
    if (!text || !clientId) return;
    const stamp = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    // Three lines, coach-approved format: date / · exercise / the note.
    const entry = `${stamp}\n· ${exerciseName}\n${text}`;
    const merged = currentNotes ? `${currentNotes}\n\n${entry}` : entry;
    try {
      await updateNotes({ id: clientId, payload: { notes: merged } }).unwrap();
      setSavedNotes(merged); // so a second note in the same session appends to the first
      setNoteDraft('');
      toast.success('Note saved to client notes');
    } catch (error) {
      toast.error(error?.data?.message || error?.message || 'Failed to save note');
    }
  };

  // Helpers to normalize media array (support legacy single image string/object)
  const getNormalizedMedia = () => {
    const media = [];
    if (data?.image) {
      const single = data.image;
      // Legacy could be string URL or object
      if (typeof single === 'string') {
        media.push({ type: 'image', url: single, fileName: single.split('/').pop() });
      } else {
        media.push({ type: (single.fileType || '').startsWith('video') ? 'video' : 'image', url: single.url || single, fileName: single.fileName });
      }
    }
    if (Array.isArray(data?.images) && data.images.length) {
      data.images.forEach((f) => {
        const isVideo = (f?.fileType || '').startsWith('video');
        media.push({ type: isVideo ? 'video' : 'image', url: f?.url || f, fileName: f?.fileName });
      });
    }
    return media;
  };

  const mediaList = getNormalizedMedia();

  // Exercise name, resolved once so the note stamp and the section heading agree.
  const exerciseName = (() => {
    const move = (data?.blockId?.moves || []).find((m) => (m?.videos || []).some((v) => v?.id === data?.moveId));
    const selected = (move?.videos || []).find((v) => v?.id === data?.moveId);
    return selected?.video?.label || 'Selected Move';
  })();

  return (
    <Dialog maxWidth="md" fullWidth open={isOpen} onClose={handleClose} aria-labelledby="move-result-global-modal">
      <DialogTitle id="move-result-global-modal">Move Result</DialogTitle>
      <DialogContent
        dividers
        sx={{
          backgroundColor: 'surface.raised',
          color: '#FFFFFF',
          padding: 0,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} sx={{ backgroundColor: '#1E1E1E' }}>
            <CircularProgress color="primary" />
          </Box>
        ) : data ? (
          <Box p={2}>
            {/* Title / Context */}
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {data?.blockId?.program?.title} • {data?.blockId?.week?.title} • {data?.blockId?.day?.title} • {data?.blockId?.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#cccccc' }}>
              Client: {data?.userId?.fullName || data?.userId?.userName || '-'}
            </Typography>

            {/* Selected move name + the instructions the coach wrote for this exercise */}
            {(() => {
              const move = (data?.blockId?.moves || []).find((m) => (m?.videos || []).some((v) => v?.id === data?.moveId));
              const videos = move?.videos || [];
              const selected = videos.find((v) => v?.id === data?.moveId);
              const heading = selected?.video?.label || 'Selected Move';
              const instruction = selected?.text;
              const instructionAr = selected?.text_ar;
              // What the coach actually PRESCRIBED. Without this the panel showed the
              // free-text note and the client's result, but never the sets/reps/hold/rest
              // being compared against.
              const prescription = formatPrescription(selected);
              return (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>{heading}</Typography>
                  {prescription || instruction || instructionAr ? (
                    <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, backgroundColor: '#2A2A2A' }}>
                      <Typography variant="subtitle2" sx={{ color: '#90caf9', mb: 0.5 }}>Your instructions</Typography>
                      {prescription ? (
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, mb: instruction || instructionAr ? 0.75 : 0, fontVariantNumeric: 'tabular-nums' }}
                        >
                          {prescription}
                        </Typography>
                      ) : null}
                      {instruction ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{instruction}</Typography>
                      ) : null}
                      {instructionAr ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', direction: 'rtl', textAlign: 'right', color: '#cccccc', mt: 0.5 }}>
                          {instructionAr}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : null}
                </>
              );
            })()}

            {/* Result text if any */}
            {data?.result ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Result</Typography>
                <Typography variant="body2" dir="auto" sx={{ whiteSpace: 'pre-wrap', unicodeBidi: 'plaintext' }}>{data?.result}</Typography>
              </Box>
            ) : null}

            {/* Media preview(s) */}
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
              {mediaList.map((file, idx) => (
                <Box key={`${file.url}-${idx}`} sx={{ width: '100%' }}>
                  {file.type === 'video' ? (
                    <video
                      controls
                      style={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 8, background: '#000' }}
                      src={file.url}
                    />
                  ) : (
                    <img
                      alt={file.fileName || 'image'}
                      style={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 8, background: '#000' }}
                      src={file.url}
                    />
                  )}
                  {/* {file.fileName ? <Typography variant="caption">{file.fileName}</Typography> : null} */}
                </Box>
              ))}
            </Box>

            {/* Coach note — written here, while watching, without closing the
                assessment. Appends to the client's Notes with date + exercise. */}
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: '#2A2A2A' }}>
              <Typography variant="subtitle2" sx={{ color: '#90caf9', mb: 1 }}>
                Coach note · {exerciseName}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Limitations, compensations, anything to remember when programming…"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                sx={{
                  backgroundColor: '#1E1E1E',
                  borderRadius: 1,
                  '& .MuiInputBase-input': { color: '#fff' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' }
                }}
              />
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  Saved into the client&apos;s Notes — coach-side only
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!noteDraft.trim() || isSavingNote}
                  onClick={() => saveNote(exerciseName)}
                >
                  {isSavingNote ? 'Saving…' : 'Save note'}
                </Button>
              </Box>
              {currentNotes ? (
                <Box sx={{ mt: 1.5, maxHeight: 140, overflowY: 'auto', p: 1, borderRadius: 1, backgroundColor: '#1E1E1E' }}>
                  <Typography variant="caption" sx={{ color: '#aaa', whiteSpace: 'pre-wrap' }}>
                    {currentNotes}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : (
          <Box p={2}><Typography variant="body2">No data found.</Typography></Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: 'surface.raised', borderTop: '1px solid', borderTopColor: 'divider', p: 2, justifyContent: 'center' }}>
        <Button onClick={handleClose} variant="contained" sx={{ backgroundColor: '#00B0FF', color: '#FFFFFF', '&:hover': { backgroundColor: '#0091EA' }, px: 4, py: 1 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalMoveResultModal;


